import { useNavigation } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useRef } from 'react'
import { Device } from '@src/config'
import { RootStoreState } from '@src/models/app.types'
import { useSnack } from '@src/utils'
import { tutorialsLastFetchedAtSelector, tutorialsListSelector } from './models/tutorials.selectors'
import { shouldRefreshTutorials } from './hooks.utils'
import { Tutorial, TutorialGroups, TutorialSensorKind } from './models/tutorials.types'

export const useTutorialNavigation = (tutorialSensorKind: TutorialSensorKind | null) => {
  const navigation = useNavigation()
  const tutorials = useSelector(tutorialsListSelector)
  const snack = useSnack()
  const shouldNavigateWhenReadyRef = useRef(false)

  useRefreshTutorials()

  const tutorialId = getTutorialIdBySensorKind(tutorialSensorKind, tutorials)

  useEffect(() => {
    if (!shouldNavigateWhenReadyRef.current || !tutorialId) {
      return
    }

    shouldNavigateWhenReadyRef.current = false
    navigation.navigate('CMSTutorial', { tutorialId })
  }, [navigation, tutorialId])

  const navigateToTutorial = useCallback(() => {
    if (!tutorialSensorKind) {
      return
    }

    if (!tutorialId) {
      if (tutorials.length === 0) {
        shouldNavigateWhenReadyRef.current = true
        return
      }

      snack('Unable to find tutorial', null, 'error')
      return
    }

    navigation.navigate('CMSTutorial', { tutorialId })
  }, [navigation, snack, tutorialId, tutorialSensorKind, tutorials.length])

  return navigateToTutorial
}

// Not used currently but keeping in place to support future use cases of navigating via Tutorial Groups
export const useTutorialGroupNavigation = (tutorialGroup?: TutorialGroups) => {
  const navigation = useNavigation()
  useRefreshTutorials()

  const navigateToTutorialGroup = useCallback(() => {
    if (tutorialGroup === undefined) {
      return
    }

    navigation.navigate('TutorialsGroup', { group: tutorialGroup })
  }, [navigation, tutorialGroup])

  return navigateToTutorialGroup
}

const useRefreshTutorials = (): void => {
  const dispatch = useDispatch()
  const snack = useSnack()
  const tutorials = useSelector(tutorialsListSelector)
  const lastFetchedAt = useSelector(tutorialsLastFetchedAtSelector)
  const isFetchingTutorials = useSelector((state: RootStoreState) =>
    Boolean(state.loading.effects['tutorials/fetchTutorials']),
  )
  const lastFailedFetchAttemptAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (
      !shouldRefreshTutorials({
        tutorialsCount: tutorials.length,
        lastFetchedAt,
        lastFailedFetchAttemptAt: lastFailedFetchAttemptAtRef.current,
        isFetching: isFetchingTutorials,
      })
    ) {
      return
    }

    dispatch({
      type: 'tutorials/fetchTutorials',
      success: () => {
        lastFailedFetchAttemptAtRef.current = null
      },
      failure: () => {
        lastFailedFetchAttemptAtRef.current = Date.now()
        snack('Failed to fetch tutorials', null, 'error')
      },
    })
  }, [dispatch, isFetchingTutorials, lastFetchedAt, snack, tutorials.length])
}

const getTutorialIdBySensorKind = (
  tutorialSensorKind: TutorialSensorKind | null,
  tutorials: Tutorial[],
) => {
  if (!tutorialSensorKind) {
    return
  }

  const matchingSensorKindTutorials = tutorials.filter(
    (tutorial) => tutorial.sensorKind === tutorialSensorKind,
  )

  if (matchingSensorKindTutorials.length === 0) {
    return
  }

  // If there's no distinction for platform, return the only matching tutorial
  if (
    matchingSensorKindTutorials.length === 1 &&
    matchingSensorKindTutorials[0].platform === null
  ) {
    return matchingSensorKindTutorials[0].id
  }

  return matchingSensorKindTutorials.find((tutorial) => tutorial.platform === Device.platform)?.id
}
